import { useState } from "react";

import { Badge, Table, type Column } from "../../components";
import type { ReviewItem } from "../../api/types";
import { t, tDynamic } from "../../i18n/t";
import { actorLabel } from "./actorLabel";
import { ItemSummary } from "./ItemSummary";
import { ResolveControl } from "./ResolveControl";
import { suggestionOf } from "./suggestions";

/** §B4.4's last sentence: each source dimension and the lines that would lose it, as review items. */
export function DimensionLoss({
  items,
  onResolve,
  disabled,
  targetKey = null,
}: {
  items: readonly ReviewItem[];
  onResolve: (itemId: string, option: string) => void;
  disabled: boolean;
  /** xmg-webux: the row "Open the tab to choose" jumped to (highlighted, scrolled to and focused). */
  targetKey?: string | null;
}): JSX.Element {
  const [chosen, setChosen] = useState<Record<string, string>>({});

  const columns: readonly Column<ReviewItem>[] = [
    {
      key: "key",
      header: t("review.col_key"),
      render: (item) => <span data-testid={`dimension-row-${item.key}`}>{item.key}</span>,
    },
    {
      key: "severity",
      header: t("review.col_severity"),
      render: (item) => (
        <Badge tone={item.severity === "BLOCK" ? "fail" : "warn"}>
          {tDynamic("severity.", item.severity)}
        </Badge>
      ),
    },
    {
      key: "message",
      header: t("review.col_message"),
      cellClassName: "cell--wide",
      render: (item) => <ItemSummary item={item} />,
    },
    {
      key: "options",
      header: t("mapping.col_options"),
      cellClassName: "cell--wide",
      render: (item) => {
        if (item.resolved_at !== null && item.resolved_at !== undefined) {
          return t("review.resolved_by", {
            actor: actorLabel(item.resolved_by),
            target: item.resolved_option ?? "",
          });
        }
        const options = item.options ?? [];
        const selectId = `dimension-option-${item.id}`;
        const picked = chosen[item.id] ?? suggestionOf(item) ?? "";
        return (
          <ResolveControl
            selectId={selectId}
            code={item.key}
            options={options}
            picked={picked}
            disabled={disabled || options.length === 0}
            resolveDisabled={disabled || picked === ""}
            onPick={(option) => setChosen({ ...chosen, [item.id]: option })}
            onResolve={() => onResolve(item.id, picked)}
          />
        );
      },
    },
  ];

  return (
    <div className="stacktable">
      <Table
        wrap
        caption={t("review.caption_dimensions")}
        columns={columns}
        rows={items}
        rowKey={(item) => item.id}
        emptyMessage={t("review.empty")}
        rowClassName={(item) => (item.key === targetKey ? "is-target" : undefined)}
        rowDataKey={(item) => item.key}
      />
    </div>
  );
}
